// frontend/src/components/Header.js
import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Search, Home, BookOpen, Menu, X, ShieldCheck, MessageSquare, MessagesSquare, FileSignature, LogIn, LogOut, UserRound } from 'lucide-react';
import { useAdmin } from '../contexts/AdminContext';
import { useAuth } from '../contexts/AuthContext';
import '../styles/logoAnimation.css';
import { prefetchPrimaryRoutesWhenIdle, prefetchRoute } from '../utils/routePrefetch';

/**
 * 헤더 컴포넌트 - 네비게이션만 포함
 */
const Header = ({ isSearchBarVisible = true }) => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isSparkling, setIsSparkling] = useState(false);
  const location = useLocation();
  const { 
    setShowAdminLogin, 
    showAdminLogin, 
    adminPassword, 
    setAdminPassword, 
    handleAdminLogin
  } = useAdmin();
  const { user, isAuthenticated, logout } = useAuth();

  // 주기적으로 반짝이는 효과 추가
  useEffect(() => {
    const sparkleInterval = setInterval(() => {
      setIsSparkling(true);
      setTimeout(() => setIsSparkling(false), 700);
    }, 5000); // 5초마다 반짝임

    return () => clearInterval(sparkleInterval);
  }, []);

  useEffect(() => prefetchPrimaryRoutesWhenIdle(), []);
  
  // 네비게이션 항목 (안전법규에 집중)
  const navItems = [
    { path: '/', label: '홈', icon: <Home size={18} /> },
    { path: '/safety-regulations', label: '안전법규', icon: <BookOpen size={18} /> },
    { path: '/safety-materials', label: '안전자재', icon: <ShieldCheck size={18} /> },
    { path: '/safety-forms', label: '안전서식', icon: <FileSignature size={18} /> },
    { path: '/field-qna', label: '현장 Q&A', icon: <MessagesSquare size={18} /> },
    { path: '/board', label: '자유게시판', icon: <MessageSquare size={18} /> }
  ];

  const isNavActive = (path) => {
    if (path === '/safety-materials') {
      return location.pathname.startsWith('/safety-materials');
    }
    if (path === '/safety-regulations') {
      return location.pathname.startsWith('/safety-regulations');
    }
    if (path === '/field-qna') {
      return location.pathname.startsWith('/field-qna');
    }
    if (path === '/safety-forms') {
      return location.pathname.startsWith('/safety-forms');
    }
    return location.pathname === path;
  };

  return (
    <header className="bg-navy-800 text-white shadow-md sticky top-0 z-50 transition-all duration-500 ease-in-out translate-y-0 opacity-100">
      {/* 상단 헤더 */}
      <div className="bg-gradient-to-r from-navy-900 to-navy-800">
        <div className="container mx-auto px-6 py-1">
          <div className="flex flex-wrap items-center justify-between">
            {/* 로고 및 사이트명 - 애니메이션 추가 */}
            <Link to="/" className="flex items-center gap-2">
              <div className="logo-glow">
                <img 
                  src="/assets/Safety-Academy_Logo.png" 
                  alt="안전사관학교 로고" 
                  className={`w-7 h-7 rounded-full shadow-sm border-2 border-gold-300 logo-animated ${isSparkling ? 'logo-blink' : ''}`}
                />
              </div>
              <h1 className="text-base md:text-lg font-bold">안전사관학교</h1>
            </Link>

            {/* 모바일 메뉴 버튼 */}
            <button 
              type="button"
              className="md:hidden p-2 rounded-lg hover:bg-green-700 transition"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              aria-label={mobileMenuOpen ? '메뉴 닫기' : '메뉴 열기'}
              aria-expanded={mobileMenuOpen}
              aria-controls="mobile-navigation"
            >
              {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>

            {/* 데스크톱 메뉴 */}
            <div className="hidden md:flex items-center gap-2">
              {/* 검색 버튼 */}
              <Link
                to="/search"
                className="p-2 rounded-full hover:bg-white/20 transition-all duration-300 flex items-center gap-1 group"
              >
                <Search size={16} className="group-hover:scale-110 transition-transform duration-300" />
                <span className="text-sm group-hover:text-white transition-colors">검색</span>
              </Link>
              {isAuthenticated ? (
                <>
                  <Link to="/account" className="flex items-center gap-1 px-2 text-sm text-green-100 hover:text-white"><UserRound size={16} />{user.username}</Link>
                  <button type="button" onClick={logout} className="flex items-center gap-1 rounded-full p-2 text-sm hover:bg-white/20"><LogOut size={16} />로그아웃</button>
                </>
              ) : (
                <Link to="/login" className="flex items-center gap-1 rounded-full p-2 text-sm hover:bg-white/20"><LogIn size={16} />로그인</Link>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* 네비게이션 */}
      <nav className="bg-navy-700 py-0.5">
        <div className="container mx-auto px-6">
          <div className="hidden md:flex overflow-x-auto hide-scrollbar py-0.5 justify-center">
            {navItems.map((item, index) => (
              <React.Fragment key={item.path}>
                {/* 메뉴 아이템 */}
                <Link
                  to={item.path}
                  onMouseEnter={() => prefetchRoute(item.path)}
                  onFocus={() => prefetchRoute(item.path)}
                  onTouchStart={() => prefetchRoute(item.path)}
                  className={`flex items-center gap-1.5 px-4 py-1.5 whitespace-nowrap mx-1 transition-all duration-300 ${
                    isNavActive(item.path)
                      ? 'bg-navy-600 font-medium rounded-lg shadow-md transform -translate-y-1 border-b-2 border-gold-400' 
                      : 'hover:bg-navy-600 hover:rounded-lg hover:shadow-md hover:transform hover:-translate-y-1'
                  }`}
                >
                  <div className={`p-0.5 rounded-full ${isNavActive(item.path) ? 'bg-gold-300/20' : ''}`}>
                    {item.icon}
                  </div>
                  <span className="relative text-sm">
                    {item.label}
                    {isNavActive(item.path) && (
                      <span className="absolute -bottom-1 left-1/2 transform -translate-x-1/2 w-1.5 h-1.5 bg-gold-300 rounded-full animate-pulse"></span>
                    )}
                  </span>
                </Link>
                
                {/* 칸막이 (마지막 아이템이 아닐 경우만) */}
                {index < navItems.length - 1 && (
                  <div className="flex items-center px-1">
                    <div className="h-4 w-0.5 bg-gray-400/30 rounded-full"></div>
                  </div>
                )}
              </React.Fragment>
            ))}
          </div>
        </div>
      </nav>

      {/* 모바일 메뉴 */}
      {mobileMenuOpen && (
        <div id="mobile-navigation" className="md:hidden bg-green-700 py-4 px-4 border-t border-green-500">
          {/* 모바일 네비게이션 */}
          <nav className="grid grid-cols-1 gap-2 mb-4">
            {navItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                onTouchStart={() => prefetchRoute(item.path)}
                onFocus={() => prefetchRoute(item.path)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg ${
                  isNavActive(item.path)
                    ? 'bg-green-800 font-medium' 
                    : 'hover:bg-green-800 transition'
                }`}
                onClick={() => setMobileMenuOpen(false)}
              >
                {item.icon}
                <span>{item.label}</span>
              </Link>
            ))}
          </nav>
          {/* 검색 버튼 */}
          <Link 
            to="/search" 
            className="px-4 py-2 hover:bg-green-600 rounded-lg transition flex items-center gap-2"
            onClick={() => setMobileMenuOpen(false)}
          >
            <Search size={18} />
            법규 검색
          </Link>
          {isAuthenticated ? (
            <button type="button" onClick={async () => { await logout(); setMobileMenuOpen(false); }} className="mt-2 flex w-full items-center gap-2 rounded-lg px-4 py-2 hover:bg-green-600"><LogOut size={18} />{user.username} · 로그아웃</button>
          ) : (
            <Link to="/login" onClick={() => setMobileMenuOpen(false)} className="mt-2 flex items-center gap-2 rounded-lg px-4 py-2 hover:bg-green-600"><LogIn size={18} />로그인·회원가입</Link>
          )}
        </div>
      )}
      
      {/* 운영자 로그인 모달 */}
      {showAdminLogin && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">운영자 인증</h3>
              <button
                type="button"
                aria-label="운영자 인증 창 닫기"
                onClick={() => {
                  setShowAdminLogin(false);
                  setAdminPassword('');
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <X size={20} />
              </button>
            </div>
            <input
              type="password"
              value={adminPassword}
              onChange={(e) => setAdminPassword(e.target.value)}
              placeholder="운영자 비밀번호를 입력하세요"
              className="w-full px-4 py-2 border-2 border-gray-300 rounded-lg mb-4 text-gray-900 bg-white focus:border-green-600 focus:outline-none"
              style={{ 
                color: '#111827',
                WebkitTextFillColor: '#111827',
                fontSize: '16px',
                letterSpacing: '2px'
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  handleAdminLogin();
                } else if (e.key === 'Escape') {
                  setShowAdminLogin(false);
                  setAdminPassword('');
                }
              }}
              autoFocus
            />
            <div className="flex gap-2">
              <button
                onClick={handleAdminLogin}
                className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
              >
                인증
              </button>
              <button
                onClick={() => {
                  setShowAdminLogin(false);
                  setAdminPassword('');
                }}
                className="flex-1 px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
              >
                취소
              </button>
            </div>
            <p className="text-xs text-gray-500 mt-3 text-center">
              운영자 전용 페이지입니다
            </p>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
